/**
 =========================================================
 * Material Dashboard 2 React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "materialDashboard/components/MDBox";

// Material Dashboard 2 React contexts
import {useMaterialUIController} from "materialDashboard/context";
import Stack from "@mui/material/Stack";

function DataTableHeadCell({width, children, sorted, align, px, py, vAlign, headerTooltip, onSortClick, verticalSeparator, ...rest}) {
    const [controller] = useMaterialUIController();
    const {darkMode} = controller;

    return (
        <Tooltip title={headerTooltip}
                 placement="top"
                 disableInteractive>
            <MDBox
                component="th"
                width={width}
                px={px}
                py={py}
                className={verticalSeparator ? "vertical-separator" : ""}
                sx={({palette: {light}, borders: {borderWidth}}) => ({
                    borderBottom: `${borderWidth[1]} solid ${light.main}`,
                    minWidth: width,
                    maxWidth: width,
                    verticalAlign: vAlign
                })}
            >
                <Stack
                    {...rest}
                    direction="row"
                    alignItems="flexStart"
                    justifyContent={align === "right" ? "flex-end" : (align === "center" ? "center" : "flex-start")}
                    color={darkMode ? "white" : "secondary"}

                    sx={({typography: {size, fontWeightBold}}) => ({
                        fontSize: size.xxs,
                        fontWeight: fontWeightBold,
                        textTransform: "uppercase",
                        userSelect: sorted && "none",
                    })}
                >
                    {children}
                    {sorted && (
                        <MDBox
                            position="relative"
                            onClick={onSortClick}
                            sx={({typography: {size}}) => ({
                                fontSize: size.lg,
                                cursor: "pointer",
                            })}>
                            <MDBox
                                position="absolute"
                                top={-9}
                                color={sorted === "asce" ? "text" : "secondary"}
                                opacity={sorted === "asce" ? 1 : 0.5}
                            >
                                <Icon>arrow_drop_up</Icon>
                            </MDBox>
                            <MDBox
                                position="absolute"
                                top={-3}
                                color={sorted === "desc" ? "text" : "secondary"}
                                opacity={sorted === "desc" ? 1 : 0.5}
                            >
                                <Icon>arrow_drop_down</Icon>
                            </MDBox>
                        </MDBox>
                    )}
                </Stack>
            </MDBox>
        </Tooltip>
    );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
    width: "auto",
    sorted: "none",
    align: "left",
    px: 3,
    py: 1.5,
    vAlign: "middle",
    verticalSeparator: false,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node.isRequired,
    sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
    align: PropTypes.oneOf(["left", "right", "center"]),
    verticalSeparator: PropTypes.bool,
};

export default DataTableHeadCell;
